import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, Shield, FileText, BarChart3, Scale,
  Loader2, AlertTriangle, Calendar, Building2, Euro,
  Upload, Download, Trash2, Sparkles, CheckCircle, XCircle, RefreshCw,
  Pencil, Check, X
} from 'lucide-react'
import { toast } from 'sonner'
import C2Sinistres from './C2Sinistres'
import ClientBudgetTab from './ClientBudgetTab'
import ClientDoctrineTab from './ClientDoctrineTab'
import PoliceFranchisesPanel from './PoliceFranchisesPanel'
import PoliceAvenantsPanel from './PoliceAvenantsPanel'
import ConservationBudgetTab from './ConservationBudgetTab'
import SuiviAmiableBlock from './SuiviAmiableBlock'
import PoliceTransportPanel from './PoliceTransportPanel'
import GrilleTarifPanel from './GrilleTarifPanel'
import { useAuth } from '../../../contexts/AuthContext'
import ImportMensuelPanel from './ImportMensuelPanel'
import TransportBudgetPanel from '../../../components/conservation/TransportBudgetPanel'

const API = process.env.REACT_APP_BACKEND_URL

function EditableDateEffet({ police, policeId, onUpdate }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(police.date_effet || '')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!value) return
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/polices/${policeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date_effet: value }),
      })
      const data = await res.json()
      if (data.success && data.police) {
        onUpdate({ date_effet: data.police.date_effet, date_echeance: data.police.date_echeance })
        toast.success(`Date d'effet mise a jour — echeance recalculee automatiquement`)
        setEditing(false)
      } else {
        toast.error(data.detail || 'Erreur')
      }
    } catch {
      toast.error('Erreur de sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div data-testid="edit-date-effet">
        <p className="text-xs text-slate-400 mb-0.5">Date d'effet</p>
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={value}
            onChange={e => setValue(e.target.value)}
            className="text-sm border border-indigo-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 w-36"
            data-testid="date-effet-input"
            autoFocus
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="p-1 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-50"
            data-testid="date-effet-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          </button>
          <button
            onClick={() => { setEditing(false); setValue(police.date_effet || '') }}
            className="p-1 rounded hover:bg-red-50 text-red-400"
            data-testid="date-effet-cancel"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <p className="text-xs text-slate-400 mb-0.5">Date d'effet</p>
      <div className="flex items-center gap-1.5 group">
        <p className="text-slate-700">{formatDate(police.date_effet)}</p>
        <button
          onClick={() => setEditing(true)}
          className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-all"
          title="Modifier la date d'effet"
          data-testid="edit-date-effet-btn"
        >
          <Pencil size={12} />
        </button>
      </div>
    </div>
  )
}

function EditablePoliceField({ label, field, value, policeId, onUpdate, type = 'text' }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value ?? '')
  const origRef = useRef(value ?? '')

  useEffect(() => { setVal(value ?? ''); origRef.current = value ?? '' }, [value])

  async function handleSave() {
    const newVal = type === 'number' ? (parseFloat(val) || null) : (val || null)
    const origVal = type === 'number' ? (parseFloat(origRef.current) || null) : (origRef.current || null)
    if (newVal === origVal) { setEditing(false); return }
    try {
      const body = {}
      body[field] = newVal
      const res = await fetch(`${API}/api/polices/${policeId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Sauvegarde automatique')
        origRef.current = val
        setEditing(false)
        if (onUpdate) onUpdate(data.police)
      } else { toast.error(data.detail || 'Erreur') }
    } catch { toast.error('Erreur reseau') }
  }

  if (editing) {
    return (
      <div>
        <p className="text-xs text-slate-400 mb-0.5">{label}</p>
        <input
          type={type} step={type === 'number' ? '0.01' : undefined}
          value={val} onChange={e => setVal(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-400"
          autoFocus onBlur={handleSave}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(origRef.current); setEditing(false) } }}
          data-testid={`edit-police-${field}`}
        />
      </div>
    )
  }

  const display = type === 'number' ? formatMontant(value) : (value || '--')
  return (
    <div className="cursor-pointer group" onClick={() => setEditing(true)}>
      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
      <p className="text-slate-700 font-medium group-hover:text-indigo-600 flex items-center gap-1">
        {display}
        <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100" />
      </p>
    </div>
  )
}

function EditablePoliceSelect({ label, field, value, policeId, options, onUpdate }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')

  useEffect(() => { setVal(value || '') }, [value])

  async function handleChange(newVal) {
    setVal(newVal)
    try {
      const body = {}
      body[field] = newVal
      const res = await fetch(`${API}/api/polices/${policeId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Sauvegarde automatique')
        setEditing(false)
        if (onUpdate) onUpdate(data.police)
      } else { toast.error(data.detail || 'Erreur') }
    } catch { toast.error('Erreur reseau') }
  }

  if (editing) {
    return (
      <div>
        <p className="text-xs text-slate-400 mb-0.5">{label}</p>
        <select
          value={val}
          onChange={e => handleChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') setEditing(false) }}
          className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-400"
          autoFocus
          data-testid={`edit-police-${field}`}
        >
          {Object.entries(options).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <div className="cursor-pointer group" onClick={() => setEditing(true)}>
      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
      <p className="text-slate-700 group-hover:text-indigo-600 flex items-center gap-1">
        {options[value] || value || '--'}
        <Pencil size={10} className="text-slate-300 opacity-0 group-hover:opacity-100" />
      </p>
    </div>
  )
}

const BASE_TABS = [
  { id: 'contrat',    label: 'Contrat',        icon: Shield },
  { id: 'sinistres',  label: 'Sinistres',      icon: FileText },
  { id: 'budget',     label: 'Budget client',  icon: BarChart3 },
  { id: 'doctrine',   label: 'Doctrine',       icon: Scale },
]

function formatDate(d) {
  if (!d) return '--'
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatMontant(m) {
  if (!m && m !== 0) return '--'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(m)
}

const TYPE_POLICE_LABELS = {
  flotte: 'Flotte automobile',
  dab: 'Dommages aux biens',
  rc_pro: 'RC Professionnelle',
  multirisque: 'Multirisque',
  cyber: 'Cyber-risques',
  protection_juridique: 'Protection juridique',
  transport: 'Transport',
  rc_transport: 'RC Transport (marchandises)',
  ad_valorem: 'Ad valorem (marchandises)',
  autre: 'Autre',
}

export default function PoliceDetail() {
  const { profile } = useAuth()
  const { tenantId, policeId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [police, setPolice] = useState(null)
  const [tenant, setTenant] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'contrat')

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setSearchParams({ tab }, { replace: true })
  }

  useEffect(() => {
    fetchPolice()
    fetchTenant()
  }, [policeId, tenantId])

  async function fetchPolice() {
    try {
      const res = await fetch(`${API}/api/polices/${policeId}`)
      if (res.ok) {
        const data = await res.json()
        setPolice(data.police || data)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  async function fetchTenant() {
    try {
      const res = await fetch(`${API}/api/admin/tenants/${tenantId}`)
      if (res.ok) {
        const data = await res.json()
        setTenant(data.tenant || data)
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!police) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <p className="text-sm text-slate-600">Police introuvable</p>
        <button
          onClick={() => navigate(`/c2/clients/${tenantId}`)}
          className="mt-3 text-xs text-indigo-600 hover:underline"
        >
          Retour au client
        </button>
      </div>
    )
  }

  const compagnieNom = police.compagnies?.nom || police.compagnie_nom || police.conditions_particulieres?.assureur || '--'
  const typeLabel = TYPE_POLICE_LABELS[police.type_police] || police.type_police || '--'
  const isFlotte = police.type_police === 'flotte'

  // Build tabs dynamically
  const isAdValorem = police.type_police === 'ad_valorem'
  const isTransport = ['rc_transport', 'ad_valorem'].includes(police.type_police)
  const TABS = isAdValorem ? [...BASE_TABS.slice(0, 2), { id: 'prime_ad_valorem', label: 'Prime ad valorem', icon: Euro }, ...BASE_TABS.slice(2)] : BASE_TABS

  async function handleDeletePolice() {
    const hasNoSinistres = !police._sinistres_count
    const msg = hasNoSinistres
      ? 'Supprimer definitivement cette police et toutes ses donnees associees (documents, garanties, primes) ? Cette action est irreversible.'
      : 'Resilier cette police ? Elle sera marquee comme resiliee mais conservee dans l\'historique.'
    if (!window.confirm(msg)) return

    try {
      const url = hasNoSinistres
        ? `${API}/api/polices/${policeId}?hard=true`
        : `${API}/api/polices/${policeId}`
      const res = await fetch(url, { method: 'DELETE' })
      const data = await res.json()
      if (res.ok) {
        toast.success(data.message || 'Police supprimee')
        navigate(`/c2/clients/${tenantId}`, { state: { tab: 'polices' } })
      } else {
        toast.error(data.detail || 'Erreur lors de la suppression')
      }
    } catch (e) {
      toast.error('Erreur reseau')
    }
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="police-detail">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-2" data-testid="police-breadcrumb">
            <button onClick={() => navigate('/c2/clients')} className="hover:text-indigo-600 transition-colors">
              Clients
            </button>
            <span>/</span>
            <button onClick={() => navigate(`/c2/clients/${tenantId}`)} className="hover:text-indigo-600 transition-colors">
              {tenant?.nom || 'Client'}
            </button>
            <span>/</span>
            <span className="text-slate-600 font-medium">Police {police.numero_police}</span>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/c2/clients/${tenantId}`, { state: { tab: 'polices' } })}
              className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
              data-testid="back-to-client"
            >
              <ArrowLeft size={18} className="text-slate-500" />
            </button>
            <div className="w-9 h-9 rounded-md bg-indigo-50 flex items-center justify-center">
              <Shield size={16} className="text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-slate-900" data-testid="police-title">
                  {police.libelle || police.numero_police}
                </h1>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  police.statut === 'en_cours' ? 'bg-emerald-100 text-emerald-700' :
                  police.statut === 'resilie' ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {police.statut === 'en_cours' ? 'En cours' :
                   police.statut === 'resilie' ? 'Resilie' :
                   police.statut || '--'}
                </span>
                <button
                  onClick={handleDeletePolice}
                  className="ml-auto p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title="Supprimer cette police"
                  data-testid="delete-police-btn"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                <span className="flex items-center gap-1">
                  <Building2 size={11} />
                  {compagnieNom}
                </span>
                <span>{typeLabel}</span>
                <span className="flex items-center gap-1">
                  <Calendar size={11} />
                  {formatDate(police.date_effet)} - {formatDate(police.date_echeance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 flex items-center gap-1 overflow-x-auto" data-testid="police-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                data-testid={`police-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'contrat' && (
          <ContratTab police={police} tenantId={tenantId} onPoliceUpdate={fetchPolice} />
        )}
        {activeTab === 'sinistres' && (
          <C2Sinistres forceTenantId={tenantId} forcePoliceId={policeId} />
        )}
        {activeTab === 'budget' && isTransport && (
          <div className="p-6"><TransportBudgetPanel tenantId={tenantId} userId={profile?.id} focusPoliceId={policeId} /></div>
        )}
        {activeTab === 'budget' && !isTransport && (
          <div className="space-y-6">
            <ClientBudgetTab tenantId={tenantId} policeId={policeId} />
            {isFlotte && <ConservationBudgetTab tenantId={tenantId} />}
            {isFlotte && <SuiviAmiableBlock tenantId={tenantId} />}
          </div>
        )}
        {activeTab === 'doctrine' && (
          <ClientDoctrineTab tenantId={tenantId} policeId={policeId} />
        )}
        {activeTab === 'prime_ad_valorem' && isAdValorem && (
          <div className="p-6 space-y-6" data-testid="prime-ad-valorem-tab">
            <GrilleTarifPanel policeId={policeId} />
            <ImportMensuelPanel policeId={policeId} userId={profile?.id} />
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Contrat Tab ─── */
function ContratTab({ police, tenantId, onPoliceUpdate }) {
  const [garanties, setGaranties] = useState([])
  const [documents, setDocuments] = useState([])
  const [loadingGaranties, setLoadingGaranties] = useState(true)
  const [loadingDocs, setLoadingDocs] = useState(true)
  const [showFranchises, setShowFranchises] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadType, setUploadType] = useState('cp')
  const [showUpload, setShowUpload] = useState(false)
  const [expandedDoc, setExpandedDoc] = useState(null)
  const fileRef = useRef(null)

  useEffect(() => {
    fetchGaranties()
    fetchDocuments()
  }, [police.id])

  async function fetchGaranties() {
    try {
      const res = await fetch(`${API}/api/polices/${police.id}`)
      if (res.ok) {
        const data = await res.json()
        setGaranties(data.police?.garanties || data.garanties || [])
      }
    } catch (e) { console.error(e) }
    finally { setLoadingGaranties(false) }
  }

  async function fetchDocuments() {
    setLoadingDocs(true)
    try {
      const res = await fetch(`${API}/api/documents-police/${police.id}`)
      if (res.ok) {
        const data = await res.json()
        setDocuments(data.documents || [])
      }
    } catch (e) { console.error(e) }
    finally { setLoadingDocs(false) }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('police_id', police.id)
      formData.append('tenant_id', tenantId)
      formData.append('type_document', uploadType)
      const res = await fetch(`${API}/api/documents-police/upload`, { method: 'POST', body: formData })
      let data
      try { data = await res.clone().json() } catch { data = { success: false, error: `Erreur serveur (${res.status})` } }
      if (!res.ok || !data.success) {
        toast.error(data.error || data.detail || `Erreur ${res.status}`)
      } else {
        toast.success('Document uploadé — extraction IA lancée')
        setShowUpload(false)
        fetchDocuments()
        setTimeout(() => { fetchGaranties(); fetchDocuments() }, 12000)
      }
    } catch (err) {
      toast.error('Erreur upload')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleExtract(docId) {
    try {
      const res = await fetch(`${API}/api/documents-police/${docId}/extract?force=true`, { method: 'POST' })
      if (res.ok) {
        toast.success('Extraction IA relancee (toutes les pages)')
        setTimeout(() => { fetchDocuments(); fetchGaranties() }, 15000)
      }
    } catch { toast.error('Erreur') }
  }

  async function handleReextractAll() {
    if (!window.confirm('Relancer l\'extraction IA de TOUS les documents de cette police ? Les garanties seront mises a jour.')) return
    try {
      const res = await fetch(`${API}/api/documents-police/police/${police.id}/reextract-all`, { method: 'POST' })
      const data = await res.json()
      if (data.success) {
        toast.success(`${data.count} document(s) en cours de re-extraction`)
        setTimeout(() => { fetchDocuments(); fetchGaranties() }, 20000)
      } else {
        toast.error(data.message || 'Erreur')
      }
    } catch { toast.error('Erreur re-extraction') }
  }

  async function handleDelete(docId) {
    if (!window.confirm('Supprimer ce document et ses donnees d\'extraction ? Cette action est irreversible.')) return
    try {
      const res = await fetch(`${API}/api/documents-police/${docId}?force=true`, { method: 'DELETE' })
      if (res.ok) {
        toast.success('Document supprime')
        fetchDocuments()
        fetchGaranties()
      } else {
        const data = await res.json().catch(() => ({}))
        toast.error(data.detail || 'Erreur suppression')
      }
    } catch { toast.error('Erreur suppression') }
  }

  async function handleDownload(docId) {
    try {
      const res = await fetch(`${API}/api/documents-police/${docId}/download-url`)
      const data = await res.json()
      if (data.url) window.open(data.url, '_blank')
    } catch { toast.error('Erreur telechargement') }
  }

  const TYPE_DOC_LABELS = { cp: 'Conditions Particulieres', cg: 'Conditions Generales', avenant: 'Avenant', attestation: 'Attestation' }
  const STATUT_COLORS = {
    en_attente: 'bg-slate-100 text-slate-500',
    en_cours: 'bg-blue-100 text-blue-600',
    termine: 'bg-emerald-100 text-emerald-700',
    erreur: 'bg-red-100 text-red-600',
  }
  const STATUT_LABELS = { en_attente: 'En attente', en_cours: 'Extraction...', termine: 'Extrait', erreur: 'Erreur' }

  return (
    <div className="p-6 space-y-6" data-testid="contrat-tab">
      {/* Police info summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Informations du contrat</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <EditablePoliceField
            label="N° Police"
            field="numero_police"
            value={police.numero_police}
            policeId={police.id}
            onUpdate={() => onPoliceUpdate?.()}
          />
          <EditablePoliceSelect
            label="Type"
            field="type_police"
            value={police.type_police}
            policeId={police.id}
            options={TYPE_POLICE_LABELS}
            onUpdate={() => onPoliceUpdate?.()}
          />
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Compagnie</p>
            <p className="text-slate-700">{police.compagnies?.nom || police.conditions_particulieres?.assureur || '--'}</p>
          </div>
          <EditablePoliceField
            label="Franchise standard"
            field="franchise_standard"
            value={police.franchise_standard}
            policeId={police.id}
            type="number"
            onUpdate={() => onPoliceUpdate?.()}
          />
          <EditableDateEffet
            police={police}
            policeId={police.id}
            onUpdate={() => onPoliceUpdate?.()}
          />
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Date d'echeance</p>
            <p className="text-slate-700">{formatDate(police.date_echeance)}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Calculee auto (effet + 1 an)</p>
          </div>
          <EditablePoliceField
            label="Plafond de garantie"
            field="plafond_garantie"
            value={police.plafond_garantie}
            policeId={police.id}
            type="number"
            onUpdate={() => onPoliceUpdate?.()}
          />
          <EditablePoliceField
            label="Libelle"
            field="libelle"
            value={police.libelle}
            policeId={police.id}
            onUpdate={() => onPoliceUpdate?.()}
          />
        </div>
        {police.notes && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-0.5">Notes</p>
            <p className="text-sm text-slate-600">{police.notes}</p>
          </div>
        )}
      </div>

      <PoliceTransportPanel police={police} tenantId={tenantId} onUpdate={() => onPoliceUpdate?.()} />

      {/* Documents police — V4 */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Documents du contrat</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{documents.length} document(s)</span>
            {documents.length > 0 && (
              <button
                onClick={handleReextractAll}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors"
                title="Relancer l'extraction IA de tous les documents"
                data-testid="reextract-all-btn"
              >
                <Sparkles size={12} />
                Re-extraire tout
              </button>
            )}
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              data-testid="upload-doc-btn"
            >
              <Upload size={12} />
              Ajouter
            </button>
          </div>
        </div>

        {/* Upload form */}
        {showUpload && (
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="upload-form">
            <div className="flex items-center gap-3 mb-2">
              <select
                value={uploadType}
                onChange={e => setUploadType(e.target.value)}
                className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
                data-testid="upload-type-select"
              >
                <option value="cp">Conditions Particulieres (CP)</option>
                <option value="cg">Conditions Generales (CG)</option>
                <option value="avenant">Avenant</option>
                <option value="attestation">Attestation</option>
              </select>
              <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-indigo-300 bg-white text-indigo-700 rounded-md cursor-pointer hover:bg-indigo-100 transition-colors">
                <Upload size={12} />
                {uploading ? 'Upload...' : 'Choisir un fichier PDF'}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="upload-file-input"
                />
              </label>
              {uploading && <Loader2 size={14} className="animate-spin text-indigo-500" />}
            </div>
            <p className="text-xs text-indigo-500">
              L'extraction IA sera lancee automatiquement apres l'upload (garanties, franchises, prime).
            </p>
          </div>
        )}

        {/* Documents list */}
        {loadingDocs ? (
          <div className="text-sm text-slate-400 py-4 text-center">Chargement...</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-6 bg-slate-50 rounded-lg">
            <FileText className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Aucun document rattache a cette police</p>
            <p className="text-xs text-slate-400 mt-1">Uploadez un CP ou CG pour alimenter les garanties et franchises</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map(doc => {
              const isExpanded = expandedDoc === doc.id
              const extraction = doc.extraction_ia || {}
              const hasExtraction = doc.statut_extraction === 'termine' && !extraction.erreur
              return (
                <div key={doc.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid="police-doc-row">
                  <div className="flex items-center gap-3 p-3 bg-white">
                    <FileText size={14} className="text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{doc.nom_fichier}</p>
                      <p className="text-xs text-slate-400">
                        {TYPE_DOC_LABELS[doc.type_document] || doc.type_document}
                        {doc.taille_octets ? ` · ${(doc.taille_octets / 1024).toFixed(0)} Ko` : ''}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[doc.statut_extraction] || 'bg-slate-100 text-slate-500'}`}>
                      {STATUT_LABELS[doc.statut_extraction] || doc.statut_extraction}
                    </span>
                    {/* Show error message + retry button for failed extractions */}
                    {(doc.statut_extraction === 'erreur' || (extraction.erreur && doc.statut_extraction !== 'termine')) && (
                      <button
                        onClick={() => handleExtract(doc.id)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-50 text-red-600 border border-red-200 rounded-full hover:bg-red-100 transition-colors"
                        title={extraction.erreur || 'Erreur extraction'}
                        data-testid="retry-extract-btn"
                      >
                        <RefreshCw size={11} />
                        Réessayer
                      </button>
                    )}
                    {doc.statut_extraction === 'en_cours' && (
                      <Loader2 size={13} className="animate-spin text-indigo-400" />
                    )}
                    <div className="flex items-center gap-1">
                      {hasExtraction && (
                        <button onClick={() => setExpandedDoc(isExpanded ? null : doc.id)} className="p-1 hover:bg-slate-100 rounded" title="Voir extraction">
                          <Sparkles size={13} className="text-amber-500" />
                        </button>
                      )}
                      <button onClick={() => handleExtract(doc.id)} className="p-1 hover:bg-slate-100 rounded" title="Relancer extraction IA" data-testid="extract-btn">
                        <RefreshCw size={13} className="text-slate-400" />
                      </button>
                      <button onClick={() => handleDownload(doc.id)} className="p-1 hover:bg-slate-100 rounded" title="Telecharger" data-testid="download-btn">
                        <Download size={13} className="text-slate-400" />
                      </button>
                      <button onClick={() => handleDelete(doc.id)} className="p-1 hover:bg-red-50 rounded" title="Supprimer" data-testid="delete-doc-btn">
                        <Trash2 size={13} className="text-red-400" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded extraction data */}
                  {isExpanded && hasExtraction && (
                    <div className="border-t border-slate-100 p-4 bg-amber-50/30 space-y-3" data-testid="extraction-details">
                      {extraction.metadata && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 mb-1">Metadonnees extraites</p>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                            {extraction.metadata.assureur && <div><span className="text-slate-400">Assureur:</span> <span className="text-slate-700 font-medium">{extraction.metadata.assureur}</span></div>}
                            {extraction.metadata.numero_police && <div><span className="text-slate-400">N° Police:</span> <span className="text-slate-700 font-medium">{extraction.metadata.numero_police}</span></div>}
                            {extraction.metadata.prime_ht_annuelle && <div><span className="text-slate-400">Prime HT:</span> <span className="text-emerald-600 font-medium">{formatMontant(extraction.metadata.prime_ht_annuelle)}</span></div>}
                            {extraction.metadata.type_contrat && <div><span className="text-slate-400">Type:</span> <span className="text-slate-700">{extraction.metadata.type_contrat}</span></div>}
                            {extraction.metadata.date_effet && <div><span className="text-slate-400">Effet:</span> <span className="text-slate-700">{extraction.metadata.date_effet}</span></div>}
                            {extraction.metadata.date_echeance && <div><span className="text-slate-400">Echeance:</span> <span className="text-slate-700">{extraction.metadata.date_echeance}</span></div>}
                          </div>
                        </div>
                      )}
                      {extraction.resume_contrat && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 mb-1">Resume</p>
                          <p className="text-xs text-slate-600 leading-relaxed">{extraction.resume_contrat}</p>
                        </div>
                      )}
                      {extraction.garanties?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 mb-1">Garanties extraites ({extraction.garanties.length})</p>
                          <div className="space-y-1">
                            {extraction.garanties.map((g, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-1.5 bg-white/60 rounded">
                                <span className="text-slate-700">{g.libelle}</span>
                                <div className="flex gap-3 text-slate-500">
                                  {g.plafond && <span>Plafond: {formatMontant(g.plafond)}</span>}
                                  {g.franchise && <span>Franchise: {formatMontant(g.franchise)}</span>}
                                  {g.page_source > 0 && <span className="text-slate-400">p.{g.page_source}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {extraction.franchises?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 mb-1">Franchises extraites ({extraction.franchises.length})</p>
                          <div className="space-y-1">
                            {extraction.franchises.map((f, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-1.5 bg-white/60 rounded">
                                <span className="text-slate-700">{f.libelle || f.type}</span>
                                <span className="text-slate-500">{f.montant} {f.unite || 'EUR'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Garanties */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Garanties</h3>
          <button
            onClick={() => setShowFranchises(!showFranchises)}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            data-testid="toggle-franchises"
          >
            {showFranchises ? 'Masquer franchises' : 'Voir franchises'}
          </button>
        </div>
        {loadingGaranties ? (
          <div className="text-sm text-slate-400 py-4 text-center">Chargement...</div>
        ) : garanties.length === 0 ? (
          <div className="text-center py-6 bg-slate-50 rounded-lg">
            <Shield className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Aucune garantie enregistree</p>
            <p className="text-xs text-slate-400 mt-1">Uploadez un CP/CG pour extraire automatiquement les garanties</p>
          </div>
        ) : (
          <div className="space-y-2">
            {garanties.map((g, i) => (
              <div key={g.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid="garantie-row">
                <div>
                  <p className="text-sm font-medium text-slate-700">{g.libelle}</p>
                  {(g.notes || g.description) && <p className="text-xs text-slate-400 mt-0.5">{g.notes || g.description}</p>}
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  {g.plafond && <span>Plafond: {formatMontant(g.plafond)}</span>}
                  {g.franchise && <span>Franchise: {formatMontant(g.franchise)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Franchises panel */}
      {showFranchises && (
        <PoliceFranchisesPanel policeId={police.id} tenantId={tenantId} />
      )}

      {/* Historique des avenants */}
      <PoliceAvenantsPanel policeId={police.id} tenantId={tenantId} onPoliceUpdate={onPoliceUpdate} />

      {/* Parametres de projection */}
      <ProjectionParamsPanel policeId={police.id} />
    </div>
  )
}


/* ─── Projection Params Panel ─── */
function ProjectionParamsPanel({ policeId }) {
  const [params, setParams] = useState({ sp_cible: '', seuil_ecretement: '', commentaire: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    fetch(`${API}/api/polices/${policeId}/projection-params`)
      .then(r => r.json())
      .then(data => {
        setParams({
          sp_cible: data.sp_cible ?? '',
          seuil_ecretement: data.seuil_ecretement ?? '',
          commentaire: data.commentaire ?? '',
        })
        if (data.sp_cible) setOpen(true)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [policeId])

  async function handleSave() {
    setSaving(true)
    try {
      const body = {}
      if (params.sp_cible !== '') body.sp_cible = parseFloat(params.sp_cible)
      if (params.seuil_ecretement !== '') body.seuil_ecretement = parseFloat(params.seuil_ecretement)
      body.commentaire = params.commentaire || null
      const res = await fetch(`${API}/api/polices/${policeId}/projection-params`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) toast.success('Parametres de projection enregistres')
      else toast.error('Erreur sauvegarde')
    } catch { toast.error('Erreur reseau') }
    finally { setSaving(false) }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="projection-params-panel">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full"
      >
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <BarChart3 size={14} className="text-indigo-600" />
          Parametres de projection
          {params.sp_cible && (
            <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full font-normal">
              S/P cible : {params.sp_cible}%
            </span>
          )}
        </h3>
        <span className="text-xs text-slate-400">{open ? 'Masquer' : 'Configurer'}</span>
      </button>
      {open && !loading && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">S/P cible (%)</label>
              <input
                type="number"
                step="0.1"
                value={params.sp_cible}
                onChange={e => setParams(p => ({ ...p, sp_cible: e.target.value }))}
                placeholder="Ex: 40"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="sp-cible-input"
              />
              <p className="text-xs text-slate-400 mt-0.5">Objectif S/P du marche pour ce contrat</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Seuil d'ecretement</label>
              <input
                type="number"
                value={params.seuil_ecretement}
                onChange={e => setParams(p => ({ ...p, seuil_ecretement: e.target.value }))}
                placeholder="Ex: 50000"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="seuil-ecretement-input"
              />
              <p className="text-xs text-slate-400 mt-0.5">Plafond par sinistre (defaut: 50 000 EUR)</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Commentaire metier</label>
              <input
                type="text"
                value={params.commentaire}
                onChange={e => setParams(p => ({ ...p, commentaire: e.target.value }))}
                placeholder="Ex: TPV chez AXA"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="commentaire-metier-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
              data-testid="save-projection-params"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
